import useUserStore from '@/store/userStore'
import emitter from '@/utils/eventBus'
import { notification } from 'ant-design-vue'
import axios from 'axios'
import FileSaver from 'file-saver'

// 使用环境变量
const apiUrl = import.meta.env.VITE_API_URL

export const request = axios.create({
  baseURL: apiUrl, // api base_url
  timeout: 1000 * 30, // 请求超时时间
})

// 是否正在刷新 Token 的标记
let isRefreshing = false
let authFailureHandled = false
// 重试队列（存储待重试的请求）
let retryQueue = []

const rejectRetryQueue = (error) => {
  retryQueue.forEach(({ reject }) => reject(error))
  retryQueue = []
}

const handleAuthFailure = (error) => {
  if (authFailureHandled) return
  authFailureHandled = true
  rejectRetryQueue(error)
  emitter.emit('request:401')
}

const parseErrorResponseData = async (data) => {
  if (!(data instanceof Blob)) return data

  try {
    const text =
      typeof data.text === 'function'
        ? await data.text()
        : await new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result)
            reader.onerror = () => reject(reader.error)
            reader.readAsText(data)
          })
    return JSON.parse(text)
  } catch {
    return {}
  }
}

request.interceptors.request.use(
  (config) => {
    const userStore = useUserStore()
    const token = userStore.getToken()
    if (token) {
      config.headers.Authorization = token
    }
    return config
  },
  (error) => {
    // 对请求错误做些什么
    return Promise.reject(error)
  },
)

request.interceptors.response.use(
  (response) => {
    if (response.config.responseType === 'blob') return response
    if (response.headers['content-type'] === 'application/json') {
      return response.data
    } else {
      return response
    }
  },
  async (error) => {
    if (error.response) {
      const userStore = useUserStore()
      const data = await parseErrorResponseData(error.response.data)
      switch (error.response.status) {
        case 401: {
          const originalRequest = error.config
          if (originalRequest._retry) {
            return Promise.reject(error)
          }
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              retryQueue.push({ resolve, reject, config: originalRequest })
            })
          }

          // 标记开始刷新
          isRefreshing = true
          originalRequest._retry = true
          try {
            const refreshTokenSuccess = await userStore.refreshToken()
            if (refreshTokenSuccess) {
              authFailureHandled = false
              retryQueue.forEach(({ resolve, reject, config }) => {
                request(config).then(resolve).catch(reject)
              })
              retryQueue = []
              return request(originalRequest)
            }
            handleAuthFailure(error)
            notification.error({
              message: '系统提示',
              description: '未认证或登录已过期，请重新登录',
            })
            return Promise.reject(error)
          } catch (e) {
            handleAuthFailure(e)
            notification.error({
              message: '系统提示',
              description: '未认证或登录已过期，请重新登录',
            })
            return Promise.reject(e)
          } finally {
            isRefreshing = false
          }
        }
        case 403:
          notification.error({
            message: '系统提示',
            description: '拒绝访问',
          })
          break
        case 404:
          notification.error({
            message: '系统提示',
            description: '资源未找到',
          })
          break
        case 500:
          notification.error({
            message: '系统提示',
            description: data.message || '内部错误',
          })
          break
        case 504:
          notification.error({
            message: '系统提示',
            description: '请求超时',
          })
          break
        default:
          notification.error({
            message: '系统提示',
            description: data.message || '内部错误',
            duration: 4,
          })
      }
    }
    return Promise.reject(error)
  },
)

//post
export function postRequest(url, parameter) {
  return request({
    url: url,
    method: 'post',
    data: parameter,
  })
}

//post method= {post | put}
export function httpRequest(url, parameter, method) {
  return request({
    url: url,
    method: method,
    data: parameter,
  })
}

//put
export function putRequest(url, parameter) {
  return request({
    url: url,
    method: 'put',
    data: parameter,
  })
}

//get
export function getRequest(url, parameter) {
  return request({
    url: url,
    method: 'get',
    params: parameter,
  })
}

//deleteRequest
export function deleteRequest(url, parameter) {
  return request({
    url: url,
    method: 'delete',
    params: parameter,
  })
}
export function deleteRequestForData(url, parameter, options = {}) {
  return request({
    url: url,
    method: 'delete',
    data: parameter,
    ...options,
  })
}
export function deleteRequestForParamsSerializer(url, parameter) {
  return request({
    url: url,
    method: 'delete',
    params: parameter,
    paramsSerializer: {
      indexes: null,
    },
  })
}

export function exportFile(url, parameter, fileName = '导出文件', method = 'get', validator) {
  const normalizedMethod = method.toLowerCase()
  return new Promise((resolve, reject) => {
    request({
      url: url,
      method: normalizedMethod,
      ...(normalizedMethod === 'get' ? { params: parameter } : { data: parameter }),
      responseType: 'blob',
    })
      .then(async (response) => {
        const blob = validator
          ? await validator(response)
          : new Blob([response.data], { type: response.headers['content-type'] })
        FileSaver.saveAs(blob, fileName)
        resolve(response)
      })
      .catch((err) => {
        reject(err)
      })
  })
}

// 上传文件
export function uploadRequest(url, formData, options = {}) {
  return request({
    url: url,
    method: 'post',
    data: formData,
    ...options,
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: options.timeout ?? 1000 * 60 * 5,
  })
}

export default {
  request,
}
